/**
 * Crew suggestions for a whole table of pending activations (RUT-3316).
 *
 * The Services → Configuration tab is where an admin works through services
 * customers activated themselves — no admin ever opened the activation dialog
 * for those, so this is the surface where the suggestion has to appear.
 *
 * ## Why this is not a request per row
 *
 * The suggestion for a row is "which crews already do this service for the
 * customers nearest this one". The service is what scopes that question, so the
 * work is grouped by SERVICE TEMPLATE, not by row: one request per distinct
 * service on screen, each carrying every customer awaiting a crew on it. A table
 * of 200 rows across 5 services costs 5 requests, not 200 — and adding rows to
 * an existing service costs nothing extra.
 *
 * Only crewless rows are asked about. A row that already has a crew is a
 * decision someone made, and the platform does not second-guess it.
 */
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { getCrews, MAX_RECOMMEND_FOR_CUSTOMERS } from '@/lib/api/routes/crews';
import { queryKeys } from '@/lib/queryKeys';
import {
  crewSuggestionKey,
  crewSuggestionsFor,
  type CrewSuggestionMap,
} from '@/components/services/crewSuggestions';
import type { EnhancedSubscription } from '@/components/services/types';

/**
 * The one definition of "ask which crews fit these customers on this service".
 *
 * Shared by every surface that wants a suggestion, so two screens can never
 * drift into asking the same question two ways — or cache the same answer under
 * two keys.
 *
 * Ids are sorted so that re-ordering the table (the Crew column is sortable)
 * never invalidates the cache.
 */
export function crewSuggestionQuery(serviceTemplateId: string, customerIds: string[]) {
  const ids = [...customerIds].sort();
  return {
    queryKey: queryKeys.crews.list({ recommendFor: { serviceTemplateId, customerIds: ids } }),
    queryFn: () => getCrews({ recommendFor: { serviceTemplateId, customerIds: ids } }),
    // The answer only moves when someone activates or re-crews this service.
    staleTime: 1000 * 60,
  };
}

/** A row we can suggest for: no crew yet, and we know its customer and service. */
function needsSuggestion(sub: EnhancedSubscription): boolean {
  return !sub.assignedCrewId && !!sub.customerId && !!sub.serviceTemplateId;
}

export interface ConfigCrewSuggestions {
  /** Keyed by `crewSuggestionKey(customerId, serviceTemplateId)`. */
  byRow: Map<string, CrewSuggestionMap>;
  /** How many requests this is costing, for anyone measuring. */
  requestCount: number;
}

export function useConfigCrewSuggestions(
  subscriptions: EnhancedSubscription[],
  options?: { enabled?: boolean },
): ConfigCrewSuggestions {
  const enabled = options?.enabled !== false;

  // Group the crewless rows by service, then split each service into requests of
  // at most the endpoint's ceiling. Chunking rather than truncating matters: the
  // tab renders up to 250 rows, and a row past a truncation point would silently
  // show no suggestion with nothing on screen explaining why.
  const batches = useMemo(() => {
    if (!enabled) return [] as { serviceTemplateId: string; customerIds: string[] }[];
    const groups = new Map<string, Set<string>>();
    for (const sub of subscriptions) {
      if (!needsSuggestion(sub)) continue;
      const set = groups.get(sub.serviceTemplateId);
      if (set) set.add(sub.customerId);
      else groups.set(sub.serviceTemplateId, new Set([sub.customerId]));
    }

    const out: { serviceTemplateId: string; customerIds: string[] }[] = [];
    for (const [serviceTemplateId, ids] of [...groups.entries()].sort((a, b) =>
      a[0].localeCompare(b[0]),
    )) {
      const sorted = [...ids].sort();
      for (let i = 0; i < sorted.length; i += MAX_RECOMMEND_FOR_CUSTOMERS) {
        out.push({
          serviceTemplateId,
          customerIds: sorted.slice(i, i + MAX_RECOMMEND_FOR_CUSTOMERS),
        });
      }
    }
    return out;
  }, [subscriptions, enabled]);

  const results = useQueries({
    queries: batches.map(({ serviceTemplateId, customerIds }) =>
      crewSuggestionQuery(serviceTemplateId, customerIds),
    ),
  });

  const byRow = useMemo(() => {
    const out = new Map<string, CrewSuggestionMap>();
    results.forEach((result, i) => {
      const batch = batches[i];
      if (!batch || !result.data?.crews?.length) return;
      for (const customerId of batch.customerIds) {
        const suggestions = crewSuggestionsFor(result.data.crews, customerId);
        // An empty map means no crew is a reasonable fit. Leaving the row out
        // says the same thing to the cell and keeps the map small.
        if (suggestions.size > 0) {
          out.set(crewSuggestionKey(customerId, batch.serviceTemplateId), suggestions);
        }
      }
    });
    return out;
    // `results` is a new array each render; its contents are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batches, results.map((r) => r.dataUpdatedAt).join(',')]);

  return { byRow, requestCount: batches.length };
}
